import { api } from "../lib/api.js";

/**
 * The page the emailed confirmation link lands on.
 *
 * Deliberately does its work on load rather than behind a button: the person
 * already clicked something to get here, and asking them to click again is a
 * step that only exists because it was easier to build.
 *
 * Reached at `#/verify?token=...`. Works signed out — someone may open the link
 * on a different device from the one they registered on, which is the common
 * case when signup happened on a desktop and the email is read on a phone.
 */
export async function verifyView(root: HTMLElement, token: string) {
  root.innerHTML = `<section class="card"><h2>Confirming your email…</h2></section>`;

  if (!token) {
    root.innerHTML = `
      <section class="card">
        <h2>That link is incomplete</h2>
        <p class="meta">Open the link from your email again, or sign in and resend it
          from Account.</p>
        <div class="actions"><a class="btn" href="#/account">Go to Account</a></div>
      </section>`;
    return;
  }

  try {
    const res = await api<{ verified: boolean; trial_ends_at: string | null }>(
      "POST", "/v1/auth/verify", { token },
    );
    const ends = res.trial_ends_at ? new Date(res.trial_ends_at) : null;

    root.innerHTML = `
      <section class="card">
        <h2>Email confirmed</h2>
        <p class="meta">Your 14-day trial has started${
          ends ? ` and runs until ${ends.toLocaleDateString()}` : ""
        }. Next: download Stream-Cut Studio and pair it with your account.</p>
        <div class="actions">
          <a class="btn" href="#/account">Connect Stream-Cut Studio</a>
          <a class="btn btn-quiet" href="#/recordings">Go to recordings</a>
        </div>
      </section>`;
  } catch (err) {
    // Expired or already-used links are the normal failure here, not an error
    // worth alarming anyone about.
    root.innerHTML = `
      <section class="card">
        <h2>That link didn't work</h2>
        <p class="meta">${(err as Error).message}</p>
        <div class="actions"><a class="btn" href="#/account">Send a new one</a></div>
      </section>`;
  }
}

// ---------------------------------------------------------------------------

interface ExpiringClip {
  id: string;
  expires_at: string;
  first_downloaded_at: string | null;
  pinned: boolean;
  ordinal: number;
  title: string | null;
  video_title: string | null;
}

interface ExpiringRecording {
  id: string;
  filename: string | null;
  expires_at: string;
  recorded_at: string | null;
}

const when = (iso: string) => {
  const days = Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
};

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

/**
 * What is about to be deleted, and the one control that stops it.
 *
 * Silent deletion is how you lose a customer who otherwise had no complaint,
 * so this exists to make retention visible before it bites rather than after.
 */
export async function expiringView(root: HTMLElement) {
  root.innerHTML = `
    <header class="page-head"><h1>Expiring soon</h1></header>
    <div id="body" class="loading">Loading…</div>`;

  const body = root.querySelector<HTMLElement>("#body")!;
  const data = await api<{ clips: ExpiringClip[]; recordings: ExpiringRecording[] }>(
    "GET", "/v1/expiring",
  );
  body.className = "";

  if (!data.clips.length && !data.recordings.length) {
    body.innerHTML = `
      <section class="card">
        <h2>Nothing expiring</h2>
        <p class="meta">Clips are kept 30 days, or 7 days after you first download one.
          Recordings go 3 days after their clips are generated.</p>
      </section>`;
    return;
  }

  body.innerHTML = `
    ${
      data.clips.length
        ? `<section class="card">
            <h2>Clips</h2>
            <p class="meta">Pin one to keep it indefinitely. Pinned clips still count
              against your storage.</p>
            <div id="clips"></div>
          </section>`
        : ""
    }
    ${
      data.recordings.length
        ? `<section class="card">
            <h2>Stream recordings</h2>
            <p class="meta">Once a recording is gone you can't re-cut its clips with
              different boundaries. The clips themselves are unaffected.</p>
            ${data.recordings
              .map(
                (r) => `<div class="row">
                  <div class="row-main">
                    <strong>${esc(r.filename ?? r.id)}</strong>
                    <span class="meta">Deleted ${when(r.expires_at)}</span>
                  </div>
                </div>`,
              )
              .join("")}
          </section>`
        : ""
    }`;

  const clipsEl = body.querySelector<HTMLElement>("#clips");
  if (!clipsEl) return;

  function renderClips(clips: ExpiringClip[]) {
    clipsEl!.innerHTML = clips
      .map(
        (c) => `<div class="row">
          <div class="row-main">
            <strong>${esc(c.title ?? `Reaction ${String(c.ordinal).padStart(3, "0")}`)}</strong>
            <span class="meta">${
              c.video_title ? esc(c.video_title) + " · " : ""
            }Deleted ${when(c.expires_at)}${
              c.first_downloaded_at ? " · already downloaded" : ""
            }</span>
          </div>
          <button class="btn btn-quiet" data-pin="${esc(c.id)}">Keep</button>
        </div>`,
      )
      .join("");

    clipsEl!.querySelectorAll<HTMLButtonElement>("[data-pin]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        btn.disabled = true;
        btn.textContent = "Keeping…";
        try {
          await api("PATCH", `/v1/clips/${btn.dataset.pin}/pin`, { pinned: true });
          // Pinned clips are no longer expiring, so drop the row rather than
          // leaving it on a list of things about to be deleted.
          btn.closest(".row")!.remove();
          if (!clipsEl!.children.length) {
            clipsEl!.innerHTML = `<p class="meta">All kept.</p>`;
          }
        } catch (err) {
          btn.disabled = false;
          btn.textContent = "Keep";
          alert((err as Error).message);
        }
      });
    });
  }

  renderClips(data.clips);
}
